/* eslint-disable @typescript-eslint/naming-convention, @typescript-eslint/no-extraneous-class */
import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, UntypedFormControl } from '@angular/forms';
import { dateIsValid, min18Years } from '../../../helpers';

@Injectable()
export class FormsValidators {
  static POSTCODE_REGEX = /^(([A-PR-UWYZ0-9][A-HK-Y0-9][AEHMNPRTVXY0-9]?[ABEHMNPRVWXY0-9]? ?){1,2}[0-9][ABD-HJLN-UW-Z]{2}|GIR 0AA)$/i;
  static MONEY_REGEX = /^\d{1,18}(\.\d{1,2})?$/;
  static EMAIL_REGEX = /(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@[*[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+]*/;
  static TELEPHONE_REGEX = /^([\(\)\-\.\\\\/+# 0-9x]|ext|extension)+$/;
  static DOMAIN_REGEX = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;

  static date(control: AbstractControl) {
    const { value } = control;
    if (value) {
      const valid = dateIsValid(value);
      return valid ? undefined : { date: true };
    }
    return { date: true };
  }

  static atLeast18Years(control: AbstractControl) {
    const { value } = control;
    if (value) {
      const valid = min18Years(value);
      return valid ? undefined : { atLeast18Years: true };
    }
    return { atLeast18Years: true };
  }

  static trueOrFalse(fc: AbstractControl) {
    const valid = fc.value === true || fc.value === false;

    return valid ? null : { required: true };
  }

  static integer(fc: AbstractControl) {
    const value = +fc.value;
    const valid = typeof value === 'number' && !isNaN(value) && Number.isInteger(value);

    return valid
      ? null
      : {
        integer: true,
      };
  }

  static number(fc: AbstractControl) {
    const value = +fc.value;
    const valid = typeof value === 'number' && !isNaN(value);

    return valid
      ? null
      : {
        number: true,
      };
  }

  static numberNotZero(fc: AbstractControl) {
    const value = +fc.value;
    const valid = typeof value === 'number' && !isNaN(value) && value > 0;

    return valid
      ? null
      : {
        numberNotZero: true,
      };
  }

  static positive(fc: AbstractControl) {
    const value = +fc.value;
    const valid = typeof value === 'number' && !isNaN(value) && value >= 0;

    return valid
      ? null
      : {
        positive: true,
      };
  }

  static noNumbersValidator(fc: AbstractControl) {
    const containsNumbersRegex = /[0-9]+/;
    if (!containsNumbersRegex.test(fc.value)) {
      return null;
    }

    return { containsNumbers: true };
  }

  static email(fc: AbstractControl) {
    const valid = fc.value && FormsValidators.EMAIL_REGEX.test(fc.value);

    return valid
      ? null
      : {
        email: true,
      };
  }

  // https://www.regexpal.com/?fam=104738
  static telephone(fc: AbstractControl) {
    const valid = fc.value && FormsValidators.TELEPHONE_REGEX.test(fc.value);

    return valid
      ? null
      : {
        telephone: true,
      };
  }

  static domain(fc: AbstractControl) {
    if (FormsValidators.DOMAIN_REGEX.test(fc.value)) {
      return null;
    }
    return {
      invalidDomain: true,
    };
  }

  static postcode(control: AbstractControl) {
    const valid = FormsValidators.POSTCODE_REGEX.test(control.value);
    return valid
      ? null
      : {
        invalidPostcode: true,
      };
  }

  static money(control: AbstractControl) {
    const valid = FormsValidators.MONEY_REGEX.test(control.value);
    return valid
      ? null
      : {
        money: true,
      };
  }

  static percentage(control: AbstractControl) {
    const value = parseFloat(control.value);
    const valid = !isNaN(value) && value >= 0 && value <= 100;
    return valid
      ? null
      : {
        percentage: true,
      };
  }

  static multipleCheckboxRequireAtLeastOne(fa: FormArray) {
    const valid = fa.controls.filter((x) => x.value).length >= 1;
    return valid
      ? null
      : {
        multipleCheckboxRequireAtLeastOne: true,
      };
  }

  static multipleCheckboxRequireMoreThanOne(fa: FormArray) {
    const valid = fa.controls.filter((x) => x.value).length > 1;
    return valid
      ? null
      : {
        multipleCheckboxRequireMoreThanOne: true,
      };
  }

  static greaterThan(min: number): any {
    return (control: UntypedFormControl) => {
      if (control.value === '' || isNaN(control.value) || control.value <= min) {
        return { greaterThan: min };
      }
      return null;
    };
  }
  static lessThan(max: number): any {
    return (control: UntypedFormControl) => {
      if (control.value === '' || isNaN(control.value) || control.value >= max) {
        return { lessThan: max };
      }
      return null;
    };
  }

  static validationExist(func: any): boolean {
    const funcStr = func.toString().replace(/\s/g, '');
    return (
      funcStr.indexOf('{required:') > -1 ||
      funcStr.indexOf('{\'required\':') > -1 ||
      funcStr.indexOf('multipleCheckboxesRequireOnlyOneChecked:') > -1 ||
      funcStr.indexOf('multipleCheckboxRequireAtLeastOne:') > -1 ||
      funcStr.indexOf('multipleCheckboxRequireMoreThanOne:') > -1
    );
  }

  static greaterThanZero(value: any) {
    value = (value || '').replace(/,/g, '');
    return value !== '' && typeof +value === 'number' && !isNaN(value) && +value > 0;
  }
}
